import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { cn } from "../lib/utils";
import { forwardRef } from "react";
import { ChevronIcon } from "../icons/chevron";

type AccordionProps =
  | AccordionPrimitive.AccordionSingleProps
  | AccordionPrimitive.AccordionMultipleProps;
type ExtendedAccordionProps = AccordionProps & {
  variant?: "primary" | "inverted";
};
type AccordionItemProps = AccordionPrimitive.AccordionItemProps;
type AccordionTriggerProps = AccordionPrimitive.AccordionTriggerProps;
type AccordionContentProps = AccordionPrimitive.AccordionContentProps;

export const Accordion = forwardRef<HTMLDivElement, ExtendedAccordionProps>(
  ({ className, variant = "primary", ...props }, ref) => {
    return (
      <AccordionPrimitive.Root
        className={cn(
          "h-fit overflow-hidden rounded-md",
          "divide-gray-4/10 divide-y",
          {
            "bg-pink": variant === "primary",
            "bg-white": variant === "inverted",
          },
          className,
        )}
        {...props}
        ref={ref}
      />
    );
  },
);

Accordion.displayName = "Accordion";

export const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ ...props }, ref) => <AccordionPrimitive.Item {...props} ref={ref} />,
);
AccordionItem.displayName = "AccordionItem";

export const AccordionTrigger = forwardRef<
  HTMLButtonElement,
  AccordionTriggerProps
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Header
    className={cn(
      "!bb-text-display-h5",
      "gap-bb flex w-full items-center justify-between",
      "px-bb py-4 lg:py-6",
      className,
    )}
  >
    <AccordionPrimitive.Trigger
      className={cn(
        "tab-focus group flex w-full items-center justify-between",
        className,
      )}
      {...props}
      ref={ref}
    >
      {props.children}
      <ChevronIcon className="transition-transform duration-200 group-data-[state=open]:rotate-180" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = "AccordionTrigger";

export const AccordionContent = forwardRef<
  HTMLDivElement,
  AccordionContentProps
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Content
    className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden"
    ref={ref}
  >
    <div
      className={cn(
        "border-gray-4/10 border-t-1",
        "px-bb py-2 lg:py-4",
        className,
      )}
      {...props}
    />
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = "AccordionContent";
